import { Accordion, Box, Flex, Icon } from '@energiebespaarders/symbols';
import { useIsTablet } from '@energiebespaarders/symbols/hooks';
import { CaretDown, CaretUp } from '@energiebespaarders/symbols/icons/line';
import { useState } from 'react';
import { Image } from 'react-datocms';
import styled from 'styled-components';
import theme from '~/styles/theme';
import type { getAllTopics_allFaqTopics } from '~/types/generated/cms/getAllTopics';
import type { getFaqItemsByCategory_allFaqItems } from '~/types/generated/cms/getFaqItemsByCategory';
import type { getSubCategories_allFaqCategories } from '~/types/generated/cms/getSubCategories';
import { StyledLink } from './ParentCategory';
import type { ReactNode } from 'react';

interface Props {
  category: getSubCategories_allFaqCategories;
  topics: ReadonlyArray<getAllTopics_allFaqTopics>;
  isInParentCategory?: boolean;
  faqItems: ReadonlyArray<getFaqItemsByCategory_allFaqItems>;
}

const HeadingContainer = styled.div<{ $isInParentCategory?: boolean }>`
  h1 {
    font-size: ${theme.type.scale[3]};
    font-weight: 600;
    margin-top: 0px;
    margin-bottom: 0px;
    font-family: Causten, sans-serif;

    @media (min-width: ${theme.breakpoints[2]}) {
      font-size: ${theme.type.scale[2]};
    }
  }

  h2 {
    margin: 0px;
    font-weight: ${x => (x.$isInParentCategory ? 500 : 600)};
  }
`;

const Heading: React.FC<{ type: 'h1' | 'h2'; text: string; isInParentCategory?: boolean }> = ({
  type,
  text,
  isInParentCategory,
}) => {
  const heading: Record<'h1' | 'h2', ReactNode> = {
    h1: <h1>{text}</h1>,
    h2: <h2>{text}</h2>,
  };

  return (
    <HeadingContainer $isInParentCategory={isInParentCategory}>{heading[type]}</HeadingContainer>
  );
};

const CategoryOverview: React.FC<Props> = ({ category, topics, isInParentCategory, faqItems }) => {
  const [expandMobile, setExpandMobile] = useState(false);

  const isTablet = useIsTablet();
  const categoryUrl = isInParentCategory
    ? `/faq/${category.parentCategory!.slug}/${category?.slug}`
    : `/faq/${category?.slug}`;

  function renderTopics() {
    return (
      <Flex flexDirection="column">
        <Flex flexWrap="wrap" mx={-1}>
          {topics.map(t => {
            const topicUrl = isInParentCategory
              ? `/faq/${category.parentCategory?.slug}/${t.category?.slug}/${t.slug}`
              : `/faq/${t.category?.slug}/${t.slug}`;

            const topicFaqitems = faqItems?.filter(i => i.topic!.id === t.id) || [];

            return (
              <Accordion
                mb={2}
                key={t.id}
                baseColor="grayBlack"
                baseBgColor="greenSlate"
                iconSize="1em"
                indicator="plus"
                title={t.title!}
                titleSize={4}
              >
                <Flex
                  style={{ backgroundColor: '#eefbfa7d', borderRadius: '0px 0px 6px 6px' }}
                  flexDirection="column"
                >
                  {topicFaqitems.map(i => (
                    <StyledLink to={`${topicUrl}#${i.id}`} style={{ marginBottom: 12 }} key={i.id}>
                      {i.question}
                    </StyledLink>
                  ))}
                </Flex>
              </Accordion>
            );
          })}
        </Flex>
      </Flex>
    );
  }

  const SubHeading = styled(Box)`
    font-size: 1.25rem;

    @media (min-width: ${theme.breakpoints[2]}) {
      font-size: ${theme.type.scale[3]};
    }
  `;

  return (
    <Box
      py={isTablet ? 2 : 8}
      style={{ borderTop: !isInParentCategory ? '' : '2px solid #eeeeee' }}
    >
      {/* HEADING AND IMAGE */}
      <Flex
        flexWrap="wrap"
        flexDirection="row"
        alignItems="center"
        mb={(!isTablet || expandMobile || !isInParentCategory) && 4}
      >
        {category.image ? (
          <Box width="60px">
            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <Image style={{ borderRadius: '100%' }} data={category.image!.responsiveImage!} />
          </Box>
        ) : (
          <></>
        )}

        <Flex
          pl={4}
          width={'calc(100% - 100px)'}
          alignItems="center"
          onClick={() => setExpandMobile(!expandMobile)}
        >
          <StyledLink to={categoryUrl}>
            <Heading
              type={isInParentCategory ? 'h2' : 'h1'}
              text={category.title!}
              isInParentCategory={isInParentCategory}
            />
          </StyledLink>
          {isTablet && isInParentCategory && (
            <Box ml="auto">
              <Icon icon={expandMobile ? CaretUp : CaretDown} width={isTablet ? '1.5em' : '2em'} />
            </Box>
          )}
        </Flex>
      </Flex>

      {category.subHeading && !isInParentCategory && (
        <SubHeading mb={4}>{category.subHeading}</SubHeading>
      )}

      {!isTablet
        ? renderTopics()
        : isTablet && expandMobile
        ? renderTopics()
        : isTablet && !isInParentCategory
        ? renderTopics()
        : null}
    </Box>
  );
};

export default CategoryOverview;
